import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Calendar } from "lucide-react";
import api from "../lib/api";
import PublicLayout from "../components/PublicLayout";
import { useLang } from "../context/LanguageContext";
import { useDashT } from "../lib/dashboardStrings";
import { setPageMeta } from "../lib/seo";

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang === "sv" ? "sv-SE" : "en-GB",
      { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
};

const Spotlight = () => {
  const { lang } = useLang();
  const dt = useDashT();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const title = "Spotlight | Talentsio";
    const desc = lang === "sv"
      ? "Insikter, trender och guider om att boka artister och underhållning i Sverige."
      : "Insights, trends and guides on booking artists and entertainment in Sweden.";
    setPageMeta({
      title, description: desc, lang,
      path: lang === "sv" ? "/sv/spotlight" : "/spotlight",
      enPath: "/spotlight", svPath: "/sv/spotlight",
    });
  }, [lang]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/spotlight", { params: { limit: 40 } })
      .then((r) => { if (alive) setPosts(r.data.posts || []); })
      .catch(() => { if (alive) setPosts([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <PublicLayout>
      <main className="max-w-[1200px] mx-auto px-6 pt-[110px] pb-20" data-testid="spotlight-index">
        <div className="flex items-center gap-2 text-[12px] tracking-[0.14em] text-[#FF7A2F] font-bold uppercase mb-3">
          <Sparkles size={14} /> {dt("TALENTSIO SPOTLIGHT")}
        </div>
        <h1 className="text-white text-[44px] leading-[1.08] font-extrabold tracking-tight mb-3 max-w-[820px]">
          {dt("Ideas, trends and guides for people who book talent")}
        </h1>
        <p className="text-neutral-400 text-[19px] leading-[1.65] max-w-[720px] mb-12">
          {dt("Hand-picked articles for organisers, wedding couples and companies who want to book the right entertainment in Sweden.")}
        </p>

        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-[#2A2A2A] bg-[#181818] overflow-hidden">
                <div className="aspect-[16/10] bg-[#1f1f1f] animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-[#2a2a2a] rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-[#2a2a2a] rounded animate-pulse w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border border-[#2A2A2A] bg-[#181818] p-12 text-center" data-testid="spotlight-empty">
            <Sparkles size={40} className="text-neutral-500 mx-auto mb-3" />
            <p className="text-white text-[18px] font-semibold mb-1">
              {dt("No articles yet")}
            </p>
            <p className="text-neutral-500 text-[14px]">
              {dt("We're working on new stories - check back soon.")}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <Link key={p.id} to={`/spotlight/${p.slug}`}
                data-testid={`spotlight-card-${p.slug}`}
                className="group rounded-2xl border border-[#2A2A2A] bg-[#181818] overflow-hidden hover:border-[#3a3a3a] hover:-translate-y-0.5 transition-all">
                <div className="aspect-[16/10] bg-[#1f1f1f] overflow-hidden relative">
                  {p.hero_image_url ? (
                    <img src={p.hero_image_url} alt={p.hero_alt || p.title}
                      loading="lazy" decoding="async"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Sparkles size={32} className="text-neutral-700" />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[11px] text-neutral-500 mb-2">
                    <Calendar size={12} /> {fmtDate(p.publish_at, p.lang)}
                    {p.tags?.[0] && <span className="ml-1 px-2 py-0.5 rounded-full bg-[#232323] text-[#FF7A2F] text-[10px] uppercase tracking-wider font-semibold">{p.tags[0]}</span>}
                  </div>
                  <h2 className="text-white text-[19px] font-bold leading-[1.25] mb-2 line-clamp-2 group-hover:text-[#FF7A2F] transition-colors">
                    {p.title}
                  </h2>
                  {p.excerpt && <p className="text-neutral-400 text-[14px] leading-[1.55] line-clamp-3">{p.excerpt}</p>}
                  <div className="mt-4 inline-flex items-center gap-1 text-[13px] text-[#FF7A2F] font-medium">
                    {dt("Read article")} <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </PublicLayout>
  );
};

export default Spotlight;
